import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupportedMime, MAX_DURATION } from './audioUtils';

const useAudioRecorder = ({ onBlobReady }) => {
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const localUrlRef = useRef('');

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    streamRef.current = null;
    analyserRef.current = null;
    setElapsed(0);
    setLevel(0);
    setError('');
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const pollLevel = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) sum += Math.abs(data[i] - 128);
    setLevel(Math.min(1, (sum / data.length / 128) * 4));
    animFrameRef.current = requestAnimationFrame(pollLevel);
  }, []);

  const startRecording = async ({ onStart, onStop, onError }) => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = getSupportedMime();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        if (localUrlRef.current) URL.revokeObjectURL(localUrlRef.current);
        localUrlRef.current = URL.createObjectURL(blob);
        setLevel(0);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        onBlobReady?.(blob);
        onStop?.(localUrlRef.current);
      };

      recorder.start(250);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => {
          if (prev + 1 >= MAX_DURATION) { recorder.stop(); clearInterval(timerRef.current); return MAX_DURATION; }
          return prev + 1;
        });
      }, 1000);
      pollLevel();
      onStart?.();
    } catch (err) {
      const msg = err.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone access and try again.'
        : `Could not access microphone: ${err.message}`;
      setError(msg);
      onError?.();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
      clearInterval(timerRef.current);
    }
  };

  return { elapsed, level, error, localUrlRef, cleanup, startRecording, stopRecording };
};

export default useAudioRecorder;
